import { Close, Delete, Edit } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, IconButton, TextField, Tooltip } from "@mui/material";
import PCheckbox from "components/PCheckbox";
import { useFormik } from "formik";
import { FormPage } from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { FC, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchForm, selectFormPages } from "redux/slices/form.slice";
import {
  createFormPage,
  deleteFormPage,
  selectLensEditorLensId,
  selectLensFormSections,
  updateFormPage,
} from "redux/slices/lensEditor";

import { P } from "theme/typography";

interface FormPageEditorProps {
  page: FormPage;
  onCancel: () => void;
}

const FormPageEditor: FC<FormPageEditorProps> = ({ page, onCancel }) => {
  const snackpatch = useAppDispatchWithSnackbar();
  const dispatch = useAppDispatch();
  const lensId = useAppSelector(selectLensEditorLensId);
  const formik = useFormik({
    initialValues: {
      name: page.name,
      rank: page.rank,
      isHidden: page.isHidden,
    },
    onSubmit: (values) => {
      snackpatch(
        updateFormPage({
          ...values,
          formId: page.formId,
          id: page.id,
          updatedAt: "",
        }),
        {
          successMessage: "Page updated",
          errorMessage: "Unable to update Page",
        }
      ).then(() => {
        void dispatch(fetchForm({ lensId }));
      });
    },
  });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 1,
        }}>
        <Box>
          <P variant="h5" sx={{ fontWeight: "bold" }}>
            Edit Page
          </P>
          <P variant="subtitle2" sx={{ color: "grey.500" }}>
            You are editing the page &quot;{page.name}&quot;
          </P>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}>
        <P variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Details
        </P>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            "& > * + *": {
              marginTop: 1.9,
            },
          }}>
          <TextField label="Page name" value={formik.values.name} onChange={formik.handleChange} name="name" />
          <TextField
            label="Page rank"
            value={formik.values.rank}
            type="number"
            onChange={formik.handleChange}
            name="rank"
          />
          <PCheckbox
            label="Hide this page"
            checked={formik.values.isHidden}
            onChange={formik.handleChange}
            name="isHidden"
          />
        </Box>
      </Box>

      <DialogActions>
        <Button color="default" onClick={onCancel}>
          Back
        </Button>
        <Button
          color="default"
          disabled={
            formik.values.name === page.name &&
            formik.values.rank === page.rank &&
            formik.values.isHidden === page.isHidden
          }
          onClick={() => {
            formik.handleSubmit();
          }}>
          Save
        </Button>
      </DialogActions>
    </>
  );
};

interface PageRowProps {
  page: FormPage;
  editPageClick: (page: FormPage) => void;
}

const PageRow: FC<PageRowProps> = ({ page, editPageClick }) => {
  const snackpatch = useAppDispatchWithSnackbar();
  const dispatch = useAppDispatch();
  const lensId = useAppSelector(selectLensEditorLensId);
  const formSections = useAppSelector(selectLensFormSections);

  const handlePageRemove = () => {
    snackpatch(
      deleteFormPage({
        id: page.id,
      }),
      {
        successMessage: "Page deleted",
        errorMessage: "Unable to delete Page",
      }
    ).then(() => {
      void dispatch(fetchForm({ lensId }));
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        py: 1,
        px: 2,
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}>
        <P variant="subtitle2">{page.name}</P>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}>
        <Tooltip title="Edit">
          <IconButton
            sx={{ display: "flex", alignItems: "center" }}
            onClick={() => {
              editPageClick(page);
            }}>
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            formSections.some((section) => section.formPageId === page.id)
              ? "Cannot delete page with sections"
              : "Delete"
          }>
          <Box>
            <IconButton
              sx={{ display: "flex", alignItems: "center" }}
              onClick={handlePageRemove}
              disabled={formSections.some((section) => section.formPageId === page.id)}>
              <Delete />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

// FormPagesList takes a editPage function as a prop

interface FormPagesListProps {
  editPageClick: (page: FormPage) => void;
}

const FormPagesList: FC<FormPagesListProps> = ({ editPageClick }) => {
  const formPages = useAppSelector(selectFormPages);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: 400,
        overflowY: "auto",
        my: 2,
        // every other row is grey
        "& > *:nth-of-type(odd)": {
          backgroundColor: "grey.50",
        },
      }}>
      {formPages.map((page) => (
        <PageRow key={page.id} page={page} editPageClick={editPageClick} />
      ))}
    </Box>
  );
};

const EditPagesDialog: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const snackpatch = useAppDispatchWithSnackbar();
  const dispatch = useAppDispatch();
  const [editingPage, setEditingPage] = useState<FormPage | null>(null);
  const formId = useAppSelector((state) => state.lensEditor.form.id);
  const lensId = useAppSelector(selectLensEditorLensId);
  const formPages = useAppSelector(selectFormPages);

  const handleAddPage = () => {
    if (!formId) return;
    // Create newPageName, a unique name for the new group based on the existing groups
    let newPageName = "New page";
    let i = 1;
    while (formPages.find((page) => page.name === newPageName)) {
      newPageName = `New page ${i}`;
      i++;
    }

    // dispatch the action to add a new group
    snackpatch(
      createFormPage({
        formId: formId,
        name: newPageName,
        rank: formPages.length,
        isHidden: false,
        id: 0,
        updatedAt: "",
      }),
      {
        successMessage: "Page created",
        errorMessage: "Unable to delete Page",
      }
    ).then(() => {
      void dispatch(fetchForm({ lensId }));
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box
        sx={{
          p: 2,
          minWidth: "400px",
        }}>
        {editingPage ? (
          <FormPageEditor page={editingPage} onCancel={() => setEditingPage(null)} />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <Box>
                <P variant="h5" sx={{ fontWeight: "bold" }}>
                  Pages
                </P>
                <P variant="subtitle2" sx={{ color: "grey.500" }}>
                  Add or remove form pages here
                </P>
              </Box>

              {/* Plus button to create group */}
              <Tooltip title="Close">
                <IconButton sx={{ display: "flex", alignItems: "center" }} onClick={() => onClose()}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
            <FormPagesList
              editPageClick={(page) => {
                setEditingPage(page);
              }}
            />
            <DialogActions>
              <Button
                color="default"
                onClick={() => {
                  handleAddPage();
                }}>
                Add page
              </Button>
            </DialogActions>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default EditPagesDialog;
